import React from 'react';

type Dot = {
  x: number;     // position in viewport units (vw)
  y: number;     // position in viewport units (vh)
  vx: number;    // velocity per frame (vw)
  vy: number;    // velocity per frame (vh)
  size: number;  // px
  hue: number;   // color hue
  alpha: number; // opacity
};

interface DotSwarmProps {
  active?: boolean;
  dotCount?: number;
}

export const DotSwarm: React.FC<DotSwarmProps> = ({ active = true, dotCount = 20 }) => {
  const [dots, setDots] = React.useState<Dot[]>([]);
  const dotsRef = React.useRef<Dot[]>([]);
  const rafRef = React.useRef<number | null>(null);
  const tickRef = React.useRef(0);

  const initDots = React.useCallback(() => {
    const makeDot = (): Dot => {
      const speed = 0.08 + Math.random() * 0.14; // vw/vh per frame (gentle drift)
      const angle = Math.random() * Math.PI * 2;
      const hue = 75 + Math.random() * 25; // maple green hue band
      return {
        x: 10 + Math.random() * 80,
        y: 10 + Math.random() * 80,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 3 + Math.random() * 6,
        hue,
        alpha: 0.22 + Math.random() * 0.35,
      };
    };
    const initial = Array.from({ length: dotCount }, makeDot);
    dotsRef.current = initial;
    setDots(initial);
  }, [dotCount]);

  React.useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    initDots();

    const animate = () => {
      const arr = dotsRef.current;
      for (let i = 0; i < arr.length; i++) {
        const d = arr[i];

        // update positions
        d.x += d.vx;
        d.y += d.vy;

        // bounce off boundaries (keep within 3vw/3vh margin)
        if (d.x < 3 || d.x > 97) d.vx *= -1;
        if (d.y < 3 || d.y > 97) d.vy *= -1;

        // gentle opacity breathing
        d.alpha += (Math.random() - 0.5) * 0.02;
        d.alpha = Math.min(0.65, Math.max(0.12, d.alpha));

        // occasional tiny hue wobble in green band
        d.hue += (Math.random() - 0.5) * 0.5;
        if (d.hue < 65) d.hue = 65;
        if (d.hue > 110) d.hue = 110;
      }

      // re-render ~30fps (skip every other frame)
      tickRef.current++;
      if (tickRef.current % 2 === 0) {
        setDots([...arr]);
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active, initDots]);

  if (!active) return null;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {dots.map((d, i) => {
        const color = `hsla(${d.hue}, 95%, 55%, ${d.alpha})`;
        const glow = `0 0 ${Math.max(8, d.size * 1.8)}px ${color}`;
        return (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              width: d.size,
              height: d.size,
              transform: `translate(${d.x}vw, ${d.y}vh)`,
              backgroundColor: color,
              boxShadow: glow,
              transition: 'transform 0.05s linear',
              willChange: 'transform, opacity',
            }}
          />
        );
      })}
    </div>
  );
};

export default DotSwarm;